import { z } from 'zod';

/**
 * Admin-config slice for contract-testing knobs. v1 only adds the
 * Git access token used by Mode B to read pacts from private
 * GitHub / GitLab repos via their raw URL endpoints.
 */
export const ContractTestingAdminConfigSchema = z.object({
  gitToken: z.string().default(''),
}).strict();
export type ContractTestingAdminConfig = z.infer<typeof ContractTestingAdminConfigSchema>;

/**
 * `pending-contract-testing` (Mode A + Mode B) — wire schemas for the
 * contract-testing surface. Mode A ships now; Mode B's `pact-verify`
 * arm rides the same schemas with `kind: 'pact-verify'` once a broker
 * is wired up.
 *
 * The structural-diff engine + classification table live in the API
 * package (`packages/api/src/lib/contract-testing/openapi-differ.ts`);
 * this file only owns the wire shape that crosses the API boundary.
 */

export const ContractCheckKindSchema = z.enum(['openapi-compare', 'pact-verify']);
export type ContractCheckKind = z.infer<typeof ContractCheckKindSchema>;

export const ContractCheckOutcomeSchema = z.enum(['passed', 'failed', 'errored']);
export type ContractCheckOutcome = z.infer<typeof ContractCheckOutcomeSchema>;

export const DiffClassificationSchema = z.enum(['breaking', 'non-breaking', 'additive', 'cosmetic', 'unclassified']);
export type DiffClassification = z.infer<typeof DiffClassificationSchema>;

export const DiffFindingSchema = z.object({
  /**
   * Spec-relative path of the affected property, e.g.
   * `paths./users.get.responses.200.content.application/json.schema.properties.id`.
   */
  path: z.string(),
  kind: z.enum(['removed', 'added', 'narrowed', 'widened', 'renamed', 'changed']),
  scope: z.enum(['endpoint', 'request', 'response', 'parameter', 'schema', 'metadata']),
  classification: DiffClassificationSchema,
  before: z.unknown().optional(),
  after: z.unknown().optional(),
});
export type DiffFinding = z.infer<typeof DiffFindingSchema>;

export const DiffSummarySchema = z.object({
  breaking: z.number().int().nonnegative().default(0),
  non_breaking: z.number().int().nonnegative().default(0),
  additive: z.number().int().nonnegative().default(0),
  cosmetic: z.number().int().nonnegative().default(0),
});
export type DiffSummary = z.infer<typeof DiffSummarySchema>;

/**
 * The default classification table is overridable per check. The
 * server stores deltas in `contract_checks.classification` JSONB;
 * Mode A ships with the defaults baked in.
 */
export const ClassificationTableSchema = z
  .object({
    removed_endpoint: DiffClassificationSchema,
    added_required_request: DiffClassificationSchema,
    removed_required_request: DiffClassificationSchema,
    added_required_response: DiffClassificationSchema,
    removed_required_response: DiffClassificationSchema,
    tightened_enum: DiffClassificationSchema,
    loosened_enum: DiffClassificationSchema,
    added_endpoint: DiffClassificationSchema,
    removed_optional: DiffClassificationSchema,
    metadata_change: DiffClassificationSchema,
  })
  .partial();
export type ClassificationTable = z.infer<typeof ClassificationTableSchema>;

export const CreateContractCheckRequestSchema = z.object({
  name: z.string().min(2).max(200),
  kind: ContractCheckKindSchema,
  baselineUrl: z.string().url().optional(),
  liveUrl: z.string().url().optional(),
  providerName: z.string().optional(),
  providerTag: z.string().optional(),
  targetUrl: z.string().url().optional(),
  classification: ClassificationTableSchema.optional(),
  scheduleCron: z.string().optional(),
});
export type CreateContractCheckRequest = z.infer<typeof CreateContractCheckRequestSchema>;

export const UpdateContractCheckRequestSchema = CreateContractCheckRequestSchema.partial().extend({
  enabled: z.boolean().optional(),
});
export type UpdateContractCheckRequest = z.infer<typeof UpdateContractCheckRequestSchema>;

export const ContractCheckRowSchema = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  project_id: z.string().uuid(),
  name: z.string(),
  kind: ContractCheckKindSchema,
  baseline_url: z.string().nullable(),
  live_url: z.string().nullable(),
  classification: ClassificationTableSchema,
  provider_name: z.string().nullable(),
  provider_tag: z.string().nullable(),
  target_url: z.string().nullable(),
  enabled: z.boolean(),
  schedule_cron: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid(),
});
export type ContractCheckRow = z.infer<typeof ContractCheckRowSchema>;

export const ContractCheckRunRowSchema = z.object({
  id: z.string().uuid(),
  check_id: z.string().uuid(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  outcome: ContractCheckOutcomeSchema,
  diff_summary: DiffSummarySchema,
  diff_findings: z.array(DiffFindingSchema),
  interactions_total: z.number().int().nullable().optional(),
  interactions_passed: z.number().int().nullable().optional(),
  interactions_failed: z.number().int().nullable().optional(),
  error_message: z.string().nullable(),
  duration_ms: z.number().int().nonnegative(),
});
export type ContractCheckRunRow = z.infer<typeof ContractCheckRunRowSchema>;

export const ListContractChecksResponseSchema = z.object({
  checks: z.array(ContractCheckRowSchema),
  lastRunByCheckId: z.record(ContractCheckRunRowSchema.partial()).optional(),
});
export type ListContractChecksResponse = z.infer<typeof ListContractChecksResponseSchema>;

export const ContractCheckDetailResponseSchema = z.object({
  check: ContractCheckRowSchema,
  recentRuns: z.array(ContractCheckRunRowSchema),
});
export type ContractCheckDetailResponse = z.infer<typeof ContractCheckDetailResponseSchema>;

/**
 * Default classification table — operators can override per-check via
 * the `classification` JSONB column.
 */
export const DEFAULT_CLASSIFICATION: Required<ClassificationTable> = {
  removed_endpoint: 'breaking',
  added_required_request: 'breaking',
  removed_required_request: 'non-breaking',
  added_required_response: 'non-breaking',
  removed_required_response: 'breaking',
  tightened_enum: 'breaking',
  loosened_enum: 'additive',
  added_endpoint: 'additive',
  removed_optional: 'additive',
  metadata_change: 'cosmetic',
};

/**
 * Minimal `ParsedSpec`-like shape the differ accepts. Mirrors the
 * existing frontend OpenAPI parser shape so both can feed the differ.
 */
export const SpecOperationSchema = z.object({
  path: z.string(),
  method: z.enum(['get', 'post', 'put', 'patch', 'delete', 'head', 'options']),
  /** True when this operation existed in the source spec. */
  exists: z.boolean().default(true),
  /** Required request fields by name. Used by the differ to detect added/removed required fields. */
  requiredRequestFields: z.array(z.string()).default([]),
  /** Required response fields (2xx) by name. */
  requiredResponseFields: z.array(z.string()).default([]),
  /** Optional enum values for path/query parameters keyed by name. */
  paramEnums: z.record(z.array(z.string())).default({}),
});
export type SpecOperation = z.infer<typeof SpecOperationSchema>;

export const ContractSpecSchema = z.object({
  title: z.string(),
  version: z.string().optional(),
  operations: z.array(SpecOperationSchema),
});
export type ContractSpec = z.infer<typeof ContractSpecSchema>;
